import React, { CSSProperties } from "react";
import { beforeAfterStyles } from "../../../config/styleConfig";

interface BeforeAfterExportProps {
  beforeImage?: string;
  afterImage?: string;
  beforeLabel?: string;
  afterLabel?: string;
  height?: number;
  borderRadius?: number;
  initialPosition?: number;
  aspectRatio?: "landscape" | "portrait" | "square" | "custom";
  maxWidth?: number;
  align?: "left" | "center" | "right";
}

export const BeforeAfterExport: React.FC<BeforeAfterExportProps> = ({
  beforeImage,
  afterImage,
  beforeLabel = "Before",
  afterLabel = "After",
  height = beforeAfterStyles.height.desktop,
  borderRadius = 12,
  initialPosition = 50,
  aspectRatio = "landscape",
  maxWidth = 100,
  align = "center",
}) => {
  const safeBeforeImage = beforeImage || beforeAfterStyles.defaultImages.before;
  const safeAfterImage = afterImage || beforeAfterStyles.defaultImages.after;
  const safeHeight = typeof height === "number" && height > 0 ? height : beforeAfterStyles.height.desktop;

  const { slider, label } = beforeAfterStyles;

  const aspectRatioMap: Record<string, string> = {
    landscape: "16 / 9",
    portrait: "3 / 4",
    square: "1 / 1",
    custom: "auto",
  };
  const cssAspectRatio = aspectRatioMap[aspectRatio] || "auto";

  const containerStyle: CSSProperties = {
    position: "relative",
    overflow: "hidden",
    userSelect: "none",
    // Let flex control width in row containers
    maxWidth: maxWidth < 100 ? `${maxWidth}%` : "100%",
    marginLeft: align === "center" ? "auto" : align === "right" ? "auto" : 0,
    marginRight: align === "center" ? "auto" : align === "left" ? "auto" : 0,
    minWidth: 0, // Allow shrinking in flex container
    ...(aspectRatio === "custom"
      ? { height: `${safeHeight}px`, minHeight: `${safeHeight}px` }
      : { aspectRatio: cssAspectRatio }),
    borderRadius: `${borderRadius}px`,
    backgroundColor: "#e5e7eb",
    boxSizing: "border-box",
  };

  const imageBaseStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const labelStyle: CSSProperties = {
    position: "absolute",
    top: `${label.top}px`,
    padding: `${label.padding.py}px ${label.padding.px}px`,
    backgroundColor: label.backgroundColor,
    color: "white",
    fontSize: label.fontSize,
    borderRadius: `${label.borderRadius}px`,
    zIndex: 5,
  };

  return (
    <div style={containerStyle} className="before-after-slider" data-initial-position={initialPosition}>
      <div style={{ ...imageBaseStyle, backgroundImage: `url('${safeAfterImage}')`, backgroundColor: "#e5e7eb" }} />
      <div
        className="before-image"
        style={{
          ...imageBaseStyle,
          backgroundImage: `url('${safeBeforeImage}')`,
          clipPath: `inset(0 ${100 - initialPosition}% 0 0)`,
        }}
      />
      <div
        className="slider-line"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: `${slider.lineWidth}px`,
          backgroundColor: "white",
          boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
          cursor: "ew-resize",
          zIndex: 10,
          left: `${initialPosition}%`,
          transform: "translateX(-50%)",
        }}
      >
        <div
          className="slider-handle"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: `${slider.handleSize}px`,
            height: `${slider.handleSize}px`,
            backgroundColor: "white",
            borderRadius: "50%",
            boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ew-resize",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
          </svg>
        </div>
      </div>
      <div style={{ ...labelStyle, left: "16px" }}>{beforeLabel}</div>
      <div style={{ ...labelStyle, right: "16px", left: "auto" }}>{afterLabel}</div>
    </div>
  );
};
